import {
  User,
  UserManager,
  UserManagerSettings,
  WebStorageStateStore,
} from 'oidc-client-ts';
import {
  getOidcApiRoot,
  getOidcAuthority,
  getOidcClientId,
  getOidcClientRoot,
  getOidcScope,
  getOidcToken,
} from '../env-vars';

export interface IAuthService {
  getUser(): Promise<User | null>;
  login(originalUrl?: string): Promise<void>;
  logout(): Promise<void>;
  renewToken(): Promise<User>;
  loginPopup(originalUrl?: string): Promise<User>;
  loginPopupCallback(): Promise<void>;
  signinCallback(): Promise<User>;
}

let _globalUserManager: UserManager;
export default class AuthService implements IAuthService {
  private _userManager: UserManager;

  constructor() {
    const settings: UserManagerSettings = {
      authority: getOidcAuthority(),
      client_id: getOidcClientId(),
      redirect_uri: `${getOidcClientRoot()}/signin-callback`,
      response_type: 'code',
      scope: getOidcScope(),
      post_logout_redirect_uri: `${getOidcClientRoot()}/signout-callback`,
      userStore: new WebStorageStateStore({ store: window.localStorage }),
      staleStateAgeInSeconds: 60,
      popup_redirect_uri: `${getOidcClientRoot()}/signin-callback`,
      accessTokenExpiringNotificationTimeInSeconds: 0,
      revokeTokensOnSignout: true,
      popupWindowFeatures: {
        menubar: false,
        toolbar: 'no',
      },
    };

    if (_globalUserManager) {
      this._userManager = _globalUserManager;
    } else {
      _globalUserManager = new UserManager(settings);
      _globalUserManager.events.addAccessTokenExpired(() => {
        if (
          window.opener &&
          window.opener !== window &&
          !window.menubar.visible
        ) {
          window.localStorage.removeItem('signinpopup');
          window.close();
        }
        if (window.localStorage.getItem('revokecall') !== 'processing') {
          window.localStorage.setItem('revokecall', 'processing');
          fetch(`${getOidcApiRoot()}/api/tickets/revoke`, {
            method: 'POST',
          })
            .then(() => {
              window.localStorage.removeItem('revokecall');
              _globalUserManager.removeUser().then();
              _globalUserManager.revokeTokens().then();
              window.localStorage.removeItem(getOidcToken());
              if (window.localStorage.getItem('signinpopup') !== 'open') {
                _globalUserManager
                  .signinRedirect({
                    state: {
                      originalUrl:
                        window.location.pathname + window.location.search,
                    },
                  })
                  .then();
              }
            })
            .catch(() => {
              window.localStorage.removeItem('signinpopup');
              window.localStorage.removeItem('revokecall');
              window.localStorage.removeItem(getOidcToken());
              if (
                window.opener &&
                window.opener !== window &&
                !window.menubar.visible
              ) {
                window.close();
              }
            });
        }
      });
      this._userManager = _globalUserManager;
    }
  }

  public getUser(): Promise<User | null> {
    return this._userManager.getUser();
  }

  public login(originalUrl?: string): Promise<void> {
    return this._userManager.signinRedirect({
      state: {
        originalUrl,
      },
    });
  }

  public loginPopup(originalUrl?: string): Promise<User> {
    window.localStorage.setItem('signinpopup', 'open');
    return this._userManager.signinPopup({
      state: {
        originalUrl,
      },
    });
  }

  public renewToken(): Promise<User> {
    return this._userManager.signinSilent();
  }

  public logout(): Promise<void> {
    const _this = this;
    window.localStorage.removeItem('revokecall');
    window.localStorage.removeItem('signinpopup');
    return fetch(`${getOidcApiRoot()}/api/tickets/revoke`, {
      method: 'POST',
    }).then(() => _this._userManager.signoutRedirect());
  }

  public signinCallback(): Promise<User> {
    window.localStorage.removeItem('revokecall');
    window.localStorage.removeItem('signinpopup');
    return this._userManager.signinRedirectCallback();
  }

  public loginPopupCallback(): Promise<void> {
    window.localStorage.removeItem('signinpopup');
    return this._userManager.signinPopupCallback();
  }

  public static revokeUserTicket(): Promise<Response> {
    return fetch(`${getOidcApiRoot()}/api/tickets/revoke`, {
      method: 'POST',
    });
  }
}
